import { useState, useEffect } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, X, UserCheck } from "lucide-react";
import { TravelRequestDetails } from "./TravelRequestDetails";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { useI18n } from "@/hooks/useI18n";
import { supabase } from "@/integrations/supabase/client";

// Tipo union per supportare entrambi i modelli
type TravelRequestType = DashboardTravelRequest | TravelRequest;

interface Designer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface TravelRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  travelRequest: TravelRequestType | null;
  onAccept?: (id: string) => Promise<boolean>;
  onDecline?: (id: string) => Promise<boolean>;
  onAssign?: (id: string, designerId: string) => Promise<boolean>;
  showActions?: boolean;
  isAdmin?: boolean;
}

export const TravelRequestModal = ({
  isOpen,
  onClose,
  travelRequest,
  onAccept,
  onDecline,
  onAssign,
  showActions = true,
  isAdmin = false,
}: TravelRequestModalProps) => {
  const { t } = useI18n();
  const [designers, setDesigners] = useState<Designer[]>([]);
  const [selectedDesignerId, setSelectedDesignerId] = useState("");
  const [isLoadingDesigners, setIsLoadingDesigners] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  useEffect(() => {
    if (!isAdmin || !isOpen) return;
    setIsLoadingDesigners(true);
    supabase
      .from("users")
      .select("id, first_name, last_name, email")
      .eq("role", "travel_designer")
      .eq("approval_status", "approved")
      .then(({ data, error }) => {
        if (!error && data) {
          setDesigners(
            data.map((d) => ({
              id: d.id,
              firstName: d.first_name ?? "",
              lastName: d.last_name ?? "",
              email: d.email,
            }))
          );
        }
        setIsLoadingDesigners(false);
      });
  }, [isAdmin, isOpen]);

  // Pre-seleziona il designer già assegnato (se presente), altrimenti quello preferito
  useEffect(() => {
    if (!travelRequest) return;
    const assignedId =
      "assignedAdvisorId" in travelRequest
        ? travelRequest.assignedAdvisorId
        : travelRequest.assigned_designer_id;
    const preferredId =
      "preferredAdvisorId" in travelRequest
        ? travelRequest.preferredAdvisorId
        : travelRequest.travel_designer_preference ?? null;
    setSelectedDesignerId(assignedId ?? preferredId ?? "");
  }, [travelRequest]);

  const preferredAdvisorId =
    travelRequest && "preferredAdvisorId" in travelRequest
      ? travelRequest.preferredAdvisorId
      : travelRequest && "travel_designer_preference" in travelRequest
        ? (travelRequest as any).travel_designer_preference ?? null
        : null;

  if (!travelRequest) return null;

  const getCreatedAt = () =>
    "createdAt" in travelRequest ? travelRequest.createdAt : travelRequest.created_at;

  const isNewRequest = (createdAt: string) => {
    const diffInHours =
      (new Date().getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
    return diffInHours < 24;
  };

  const handleAccept = async () => {
    if (!onAccept) return;
    const success = await onAccept(travelRequest.id);
    if (success) onClose();
  };

  const handleDecline = async () => {
    if (!onDecline) return;
    const success = await onDecline(travelRequest.id);
    if (success) onClose();
  };

  const handleAssign = async () => {
    if (!onAssign || !selectedDesignerId) return;
    setIsAssigning(true);
    const success = await onAssign(travelRequest.id, selectedDesignerId);
    setIsAssigning(false);
    if (success) onClose();
  };

  const adminFooter = (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-2">
        <Select
          value={selectedDesignerId}
          onValueChange={setSelectedDesignerId}
          disabled={isLoadingDesigners}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder={t("travelRequest.selectAdvisor")} />
          </SelectTrigger>
          <SelectContent>
            {designers.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                <span className="flex items-center gap-2">
                  {d.firstName} {d.lastName} ({d.email})
                  {preferredAdvisorId && d.id === preferredAdvisorId && (
                    <Badge variant="secondary" className="text-xs ml-1">
                      {t("travelRequest.preferred")}
                    </Badge>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="default"
          onClick={handleAssign}
          disabled={!selectedDesignerId || isAssigning}
        >
          <UserCheck className="h-4 w-4" />
          {t("travelRequest.assign")}
        </Button>
      </div>
    </div>
  );

  const designerFooter = (
    <>
      <Button variant="outline" onClick={handleDecline}>
        <X className="h-4 w-4" /> {t("travelRequest.decline")}
      </Button>
      <Button variant="default" onClick={handleAccept}>
        <Check className="h-4 w-4" /> {t("travelRequest.accept")}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <span>
            {t("travelRequest.title")}{" "}
            {travelRequest.destinations.length > 0
              ? travelRequest.destinations.join(", ")
              : t("travelRequest.destinationTBD")}
          </span>
          {isNewRequest(getCreatedAt()) && (
            <Badge variant="default" className="text-xs px-2 py-1 rounded-full">
              {t("common.new")}
            </Badge>
          )}
        </div>
      }
      size="lg"
      footer={showActions ? (isAdmin ? adminFooter : designerFooter) : null}
    >
      <TravelRequestDetails travelRequest={travelRequest} />
    </Modal>
  );
};
